<template>
  <div class="wrapper">
    <div class="app" :class="{ showFilter, showList, showLegend }">
      <Map>
        <View
          ref="view"
          :position="position"
          :projection="this.settings.projection"
          :padding="padding"
          @position-changed="onPositionChanged"
        />
        <SelectInteraction
          :selected="selectedFeature"
          :styleName="this.settings.styleName"
          @select="onSelectFeature"
          @zoom-to="onZoomTo"
        />
        <div v-bind:key="layer.name" v-for="layer in settings.layers">
          <MaplibreLayer
            v-if="layer.type === 'MAPLIBRE'"
            :name="layer.name"
            :options="layer.options"
          />
          <WMTSLayer
            v-if="layer.type === 'WMTS'"
            :name="layer.name"
            :url="layer.url"
          />
          <VectorLayer
            v-if="layer.type === 'VECTOR'"
            :name="layer.name"
            :clusterItems="layer.clusterItems"
            :styleName="layer.styleName || settings.styleName"
            :features="
              layer.name == 'schools'
                ? displaySchools
                : layerUrlData[layer.name]
            "
            :selectable="layer.selectable"
            @select="onSelectFeature"
          />
        </div>
      </Map>

      <div class="toggle-buttons">
        <div
          class="filter-button"
          v-if="
            this.settings.features.filter &&
            !this.showFilter &&
            !this.showSingleItem
          "
        >
          <Button size="large" label="Verfijn" dropShadow @click="toggleFilter">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height="24px"
              viewBox="0 0 24 24"
              width="24px"
              fill="#000000"
            >
              <path d="M0 0h24v24H0V0z" fill="none" />
              <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
            </svg>
          </Button>
          <div v-if="activeFilterCount > 0" class="filter-count">
            {{ activeFilterCount }}
          </div>
        </div>
        <Button
          v-if="
            this.settings.features.listDisplay &&
            !this.showList &&
            !this.showSingleItem
          "
          size="large"
          label="Lijst"
          dropShadow
          @click="toggleList"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            enable-background="new 0 0 24 24"
            height="24px"
            viewBox="0 0 24 24"
            width="24px"
            fill="#000000"
          >
            <rect fill="none" height="24" width="24" />
            <path
              d="M3,5v14h18V5H3z M7,7v2H5V7H7z M5,13v-2h2v2H5z M5,15h2v2H5V15z M19,17H9v-2h10V17z M19,13H9v-2h10V13z M19,9H9V7h10V9z"
            />
          </svg>
        </Button>
        <Button
          v-if="this.settings.features.legendDisplay && !this.showLegend"
          size="large"
          label="Legenda"
          dropShadow
          @click="toggleLegend"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            height="24px"
            viewBox="0 -960 960 960"
            width="24px"
            fill="#000000"
          >
            <path
              d="M440-280h80v-240h-80v240Zm40-320q17 0 28.5-11.5T520-640q0-17-11.5-28.5T480-680q-17 0-28.5 11.5T440-640q0 17 11.5 28.5T480-600Zm0 520q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z"
            />
          </svg>
        </Button>
      </div>

      <ListPanel
        ref="listPanel"
        v-if="showList && !selectedFeature"
        class="list-panel"
        :marker="
          this.marker
            ? { geometry: { type: 'Point', coordinates: this.marker } }
            : null
        "
        :title="this.settings.title"
        :titleField="this.settings.titleField"
        :descriptionField="this.settings.descriptionField"
        :schools="displaySchools"
        @hidePanel="showList = false"
        @select="onSelectFeature"
      />

      <DetailPanel
        ref="detailPanel"
        v-if="selectedFeature"
        :feature="selectedFeature"
        :fields="this.settings.detailFields"
        :showSingleItem="showSingleItem"
        @hideDetailAndListPanel="hideDetailAndListPanel"
        @hideDetailPanel="clearSelectedFeature"
      />

      <FilterPanel
        v-if="showFilter"
        ref="filterPanel"
        :facets="facets"
        v-model="filters"
        class="filter-panel"
        @hidePanel="hideFilterPanel"
      />

      <LegendPanel
        v-if="showLegend"
        ref="legendPanel"
        :legend="this.settings.legend"
        class="legend-panel"
        @hidePanel="hideLegendPanel"
      />

      <ZoomPanel :position="position" @adjust-zoom="adjustZoom" />
    </div>
  </div>
</template>

<script>
import proj4 from "proj4";
import GeoJSON from "ol/format/GeoJSON";
import { boundingExtent } from "ol/extent";

import Map from "./components/Map/Map.vue";
import View from "./components/Map/View.vue";
import Button from "./components/Button.vue";
import SelectInteraction from "./components/Map/SelectInteraction.vue";
import WMTSLayer from "./components/Map/WMTSLayer.vue";
import MaplibreLayer from "./components/Map/MaplibreLayer.vue";
import VectorLayer from "./components/Map/VectorLayer.vue";
import ListPanel from "./components/ListPanel.vue";
import DetailPanel from "./components/DetailPanel.vue";
import FilterPanel from "./components/FilterPanel.vue";
import LegendPanel from "./components/LegendPanel.vue";
import ZoomPanel from "./components/Map/ZoomPanel.vue";
import { getData } from "./services";

const geoJSON = new GeoJSON();

export default {
  name: "MapPage",
  components: {
    Map,
    View,
    Button,
    SelectInteraction,
    WMTSLayer,
    MaplibreLayer,
    VectorLayer,
    ListPanel,
    DetailPanel,
    FilterPanel,
    LegendPanel,
    ZoomPanel,
  },
  props: {
    settings: Object,
  },
  provide() {
    return {
      settings: this.settings,
    };
  },
  async mounted() {
    const urlSearchParams = new URLSearchParams(window.location.search);

    if (this.settings.url) {
      const result = await fetch(this.settings.url);
      const data = await result.json();

      let items = [];

      if (data.records) {
        items = data.records;
      } else if (data.features) {
        items = data.features;
      } else if (Array.isArray(data)) {
        items = data;
      }

      items = items.map((item, index) => {
        let geometry = item.geometry;
        if (
          this.settings.urlProjection &&
          this.settings.urlProjection !== this.settings.projection
        ) {
          geometry = geoJSON.readGeometry(item.geometry, {
            dataProjection: this.settings.urlProjection,
            featureProjection: this.settings.projection,
          });
          geometry = geoJSON.writeGeometryObject(geometry);
        }

        return {
          ...item,
          ...(item.properties ? item.properties : {}),
          id: `schools-${item.id ? item.id : index}`,
          geometry,
        };
      });

      this.allSchools = items;
    } else {
      this.allSchools = await getData(
        this.settings.tableName,
        this.settings.fields
      );
    }

    this.settings.layers.forEach(async (layer) => {
      if (!layer.url) {
        return;
      }

      if (layer.type !== 'VECTOR') {
        return;
      }

      const result = await fetch(layer.url);
      const data = await result.json();

      let items = [];
      if (data.records) {
        items = data.records;
      }

      if (
        this.settings.urlProjection &&
        this.settings.urlProjection !== this.settings.projection
      ) {
        items = items.map((item, index) => {
          let geometry = item.geometry;
          if (
            this.settings.urlProjection &&
            this.settings.urlProjection !== this.settings.projection
          ) {
            geometry = geoJSON.readGeometry(item.geometry, {
              dataProjection: this.settings.urlProjection,
              featureProjection: this.settings.projection,
            });
            geometry = geoJSON.writeGeometryObject(geometry);
          }

          return {
            ...item,
            ...(item.properties ? item.properties : {}),
            id: `${layer.name}-${item.id ? item.id : index}`,
            geometry,
          };
        });
      }

      this.layerUrlData[layer.name] = items;
    });

    const requestedUids = urlSearchParams.getAll("uid");
    if (requestedUids.length > 0) {
      const matchingInstances = this.allSchools.filter(
        (school) => requestedUids.includes(String(school.uid))
      );
      if (matchingInstances.length > 0) {
        this.showSingleItem = matchingInstances.length === 1;
        this.allSchools = matchingInstances;
      }
    }

    const facets = this.settings.facets;

    facets.forEach((facet) => {
      const rawValues = [
        ...new Set(this.allSchools.map((school) => school[facet.key])),
      ]
        .filter((v) => v !== null)
        .filter((v) => v !== "")
        .sort();

      facet.values = rawValues;

      if (typeof facet.values[0] === "object") {
        facet.values = [...new Set([].concat.apply([], facet.values))].sort();
      }
    });

    this.facets = facets;

    const filters = {};
    facets.forEach((facet) => {
      const initialFilterRequestedValues = urlSearchParams.getAll(facet.key);
      if (initialFilterRequestedValues.length === 0) {
        return;
      }

      const intialFilterValues = facet.values.filter((x) =>
        initialFilterRequestedValues.includes(x)
      );
      if (intialFilterValues.length > 0) {
        filters[facet.key] = intialFilterValues;
      }
    });
    this.filters = filters;

    const geometries = this.displaySchools.map((school) =>
      geoJSON.readGeometry(school.geometry).getCoordinates()
    );

    if (geometries.length === 1) {
      this.$refs.view.centerOn(geometries[0], {
        padding: this.padding,
      });
      return;
    }

    if (geometries.length > 1) {
      this.$refs.view.fit(boundingExtent(geometries), {
        padding: this.padding,
      });
      return;
    }
  },
  data() {
    return {
      position: { zoom: 18, center: [924582, 5950164] },
      padding: [25, 25, 25, 25],
      marker: null,
      allSchools: [],
      highlightedSchools: [],
      facets: [],
      filters: {},
      layerUrlData: {},
      selectedFeature: null,
      showSingleItem: false,
      showFilter: false,
      showList: false,
      showLegend: false,
    };
  },
  created() {
    window.addEventListener("resize", this.onResizeWindow);
  },
  destroyed() {
    window.removeEventListener("resize", this.onResizeWindow);
  },
  computed: {
    displaySchools() {
      return this.allSchools
        .map((school) => {
          return {
            ...school,
            highlighted: this.highlightedSchools.includes(school.naam),
          };
        })
        .filter((school) => {
          let display = true;
          Object.keys(this.filters).forEach((filterKey) => {
            if (this.filters[filterKey].length === 0) {
              return;
            }

            if (Array.isArray(school[filterKey])) {
              if (
                school[filterKey].filter((x) =>
                  this.filters[filterKey].includes(x)
                ).length === 0
              ) {
                display = false;
              }
            } else if (school[filterKey] === null) {
              if (!this.filters[filterKey].includes("")) {
                display = false;
              }
            } else {
              if (!this.filters[filterKey].includes(school[filterKey])) {
                display = false;
              }
            }
          });

          return display;
        });
    },
    activeFilterCount() {
      let activeFilterCount = 0;

      Object.keys(this.filters).forEach((filterKey) => {
        activeFilterCount += this.filters[filterKey].length;
      });

      return activeFilterCount;
    },
  },
  methods: {
    setPosition(position) {
      this.$refs.view.centerOn(position, {
        padding: this.padding,
        duration: 250,
      });
    },
    adjustZoom(delta, options) {
      this.$refs.view.adjustZoom(delta, options);
    },
    onPositionChanged(position) {
      this.position = position;
    },
    setMarker(marker) {
      this.marker = marker;
    },
    onSelectFeature(feature) {
      this.selectedFeature = feature;

      if (feature !== null) {
        if (this.settings.features.listDisplay && !this.showSingleItem) {
          this.showList = true;
        } else {
          this.showList = false;
        }

        this.showLegend = false;
        this.showFilter = false;
        this.showSingleItem = false;

        this.$nextTick(() => {
          this.$nextTick(() => {
            this.$refs.view.centerOn(feature.geometry.coordinates, {
              padding: this.padding,
              duration: 250,
            });
          });
        });
      }
    },
    onZoomTo(feature) {
      if (feature !== null) {
        this.$nextTick(() => {
          this.$nextTick(() => {
            this.$refs.view.centerOn(feature.geometry.coordinates, {
              padding: this.padding,
              duration: 500,
            });
            this.$refs.view.adjustZoom(2);
          });
        });
      }
    },
    clearSelectedFeature() {
      this.selectedFeature = null;
    },
    clearQuery() {
      this.marker = null;
      this.highlightedSchools = [];
    },
    hideDetailAndListPanel() {
      this.showList = false;
      this.selectedFeature = null;
    },
    hideFilterPanel() {
      this.showFilter = false;
    },
    hideLegendPanel() {
      this.showLegend = false;
    },
    highlightSchools(highlightedSchools, marker) {
      this.highlightedSchools = highlightedSchools;
      this.filters = {};

      if (this.settings.features.listDisplay && !this.showSingleItem) {
        this.showList = true;
      }

      const geometries = this.displaySchools
        .filter((school) => school.highlighted)
        .map((school) =>
          geoJSON.readGeometry(school.geometry).getCoordinates()
        );

      geometries.push(marker);

      this.$nextTick(() => {
        this.$nextTick(() => {
          this.$refs.view.fit(boundingExtent(geometries), {
            padding: this.padding,
            duration: 250,
          });
        });
      });
    },
    toggleFilter() {
      this.showList = false;
      this.showLegend = false;
      this.showFilter = true;
      this.showSingleItem = false;
      this.clearSelectedFeature();
    },
    toggleList() {
      this.showList = true;
      this.showLegend = false;
      this.showFilter = false;
      this.showSingleItem = false;
      this.clearSelectedFeature();
    },
    toggleLegend() {
      this.showList = false;
      this.showLegend = true;
      this.showFilter = false;
      this.showSingleItem = false;
      this.clearSelectedFeature();
    },
    updatePadding() {
      let top = 25;
      let right = 25;
      let bottom = 25;
      let left = 25;

      if (window.innerWidth <= 576) {
        if (this.$refs.listPanel) {
          bottom += this.$refs.listPanel.$el.clientHeight;
        } else if (this.$refs.detailPanel) {
          bottom += this.$refs.detailPanel.$el.clientHeight;
        } else if (this.$refs.filterPanel) {
          bottom += this.$refs.filterPanel.$el.clientHeight;
        }
      } else {
        if (this.$refs.listPanel) {
          left += this.$refs.listPanel.$el.clientWidth;
        } else if (this.$refs.detailPanel) {
          left += this.$refs.detailPanel.$el.clientWidth;
        }

        if (this.$refs.filterPanel) {
          left += this.$refs.filterPanel.$el.clientWidth;
        }
      }

      this.padding = [top, right, bottom, left];
    },
    onResizeWindow() {
      this.updatePadding();
    },
  },
  watch: {
    filters(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.clearSelectedFeature();
      }
    },
    showFilter() {
      this.$nextTick(() => {
        this.updatePadding();
      });
    },
    async showList() {
      this.$nextTick(() => {
        this.updatePadding();
      });
    },
  },
};
</script>

<style>
:root {
  --font-size-tiny: 14px;
  --font-size-small: 14px;
  --font-size-normal: 16px;
  --font-size-large: 18px;

  --font-weight-normal: 400;
  --font-weight-bold: 700;

  --color-primary: #24578f;

  --color-text-grey: rgba(0, 0, 0, 0.55);

  --color-hover: rgba(0, 0, 0, 0.03);
  --color-active: rgba(0, 0, 0, 0.06);

  --color-grey-10: #f5f5f5; /* background */
  --color-grey-20: #eaeaea; /* divider (list items, files) */
  --color-grey-30: #dddddd; /* divider (sections, header) */
  --color-grey-40: #bfbfbf; /* button border, tag border */
  --color-grey-50: #949494; /* input border (text, checkbox, switch, etc.) */

  --color-icon-grey: rgba(0, 0, 0, 0.42);

  --radius-small: 4px;
  --radius-normal: 6px;
  --shadow-normal: 0 0 1px rgba(0, 0, 0, 0.2), 0 0 8px rgba(0, 0, 0, 0.15);

  --padding-screen: 20px;
  --panel-width: 100%;
}

@media (min-width: 576px) {
  :root {
    --panel-width: 320px;
  }
}

/* @media (min-width: 768px) {
    :root {
    }
}

@media (min-width: 1200px) {
    :root {
    }
} */

html,
body {
  width: 100%;
  height: 100%;
}

html {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica,
    Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
  font-size: var(--font-size-normal);
  font-weight: var(--font-weight-normal);
}

body {
  margin: 0;
  word-break: break-word;
}

button {
  text-align: left;
  font: inherit;
}

button:not([disabled]) {
  cursor: pointer;
}

ul {
  margin: 0;
  padding: 0;
  list-style-type: none;
}

#app {
  width: 100%;
  height: 100%;
}

[data-tippy-root] {
  padding: 0;
  border-radius: var(--radius-normal);
  font-family: inherit;
  font-size: var(--font-size-tiny);
  font-weight: var(--font-weight-bold);
  letter-spacing: 0em;
}

[data-tippy-root] .tippy-content {
  padding: 3px 7px 4px;
}

[data-tippy-root].dark-theme .tippy-backdrop {
  background-color: var(--color-tooltip-dark);
}

[data-tippy-root].primary-theme .tippy-backdrop {
  /* TODO: var(--color-primary) doesn't work */
  background-color: #0066ff;
}

[data-tippy-root].popover-theme {
  background-color: white;
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-normal);
  color: #000000;
  letter-spacing: inherit;
  box-shadow: var(--shadow-normal);
}

[data-tippy-root].popover-theme[x-placement^="left"] .tippy-arrow {
  border-left-color: white;
}

[data-tippy-root].popover-theme[x-placement^="right"] .tippy-arrow {
  border-right-color: white;
}

[data-tippy-root].popover-theme .tippy-content {
  padding: 0;
  overflow: auto;
}
</style>

<style scoped>
.wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.content {
  padding: 24px;
  border-bottom: 1px solid var(--color-grey-30);
}

.content h2 {
  margin: 0;
}

.app {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  overflow: hidden;
}

.search {
  padding: var(--padding-screen);
}

.panel {
  z-index: 1;
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  max-width: var(--panel-width);
}

@media (max-width: 575px) {
  .panel {
    height: 50%;
  }

  .panel.expand {
    height: 100%;
  }

  .app.showFilter .zoom-panel,
  .app.showList .zoom-panel,
  .app.showLegend .zoom-panel {
    bottom: calc(50% + var(--padding-screen));
  }
}

@media (min-width: 576px) {
  .panel {
    height: 100%;
  }

  .app.showFilter .list-panel,
  .app.showFilter .detail-panel {
    left: var(--panel-width);
  }

  .app.showFilter .toggle-buttons,
  .app.showList .toggle-buttons,
  .app.showLegend .toggle-buttons {
    left: calc(var(--padding-screen) + var(--panel-width));
  }

  .app.showFilter.showList .toggle-buttons {
    left: calc(var(--padding-screen) + var(--panel-width) * 2);
  }
  .app.showFilter.showList.showLegend .toggle-buttons {
    left: calc(var(--padding-screen) + var(--panel-width) * 3);
  }

  .app.showList .filter-panel {
    box-shadow: 1px 0 0 var(--color-grey-30);
  }
}

.zoom-panel {
  position: absolute;
  right: var(--padding-screen);
  bottom: var(--padding-screen);
}

.content {
  flex: 1 1 0%;
  position: relative;
}

.map {
  width: 100%;
  height: 100%;
}

.toggle-buttons {
  position: absolute;
  top: var(--padding-screen);
  left: var(--padding-screen);
  display: flex;
}

.toggle-buttons > *:not(:last-child) {
  margin-right: 8px;
}

.filter-button {
  display: flex;
}

.filter-count {
  position: relative;
  flex-shrink: 0;
  margin-top: 2px;
  margin-left: -9px;
  box-sizing: border-box;
  height: 18px;
  min-width: 18px;
  border-radius: 9px;
  border: 2px solid var(--color-primary);
  padding: 0 3px;
  background: white;
  color: var(--color-primary);
  font-size: 11px;
  font-weight: var(--font-weight-bold);
  line-height: 14px;
  text-align: center;
  white-space: nowrap;
  user-select: none;
}
</style>
